import { useLocation } from 'react-router-dom';
import { getBasePath, assetUrl } from '../../blog/urls';
import { motion } from 'framer-motion';
import useReveal from '../../utils/useReveal';
import styled from 'styled-components';
import {
  FaFacebookF,
  FaGithub,
  FaInstagram,
  FaLinkedinIn,
  FaPaperPlane,
  FaTwitter,
} from 'react-icons/fa';
import { FiArrowUpRight, FiDownload } from 'react-icons/fi';
import { Bio } from '../../data/constants';

const FooterContainer = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 62px 20px 30px;
  overflow: hidden;
  background:
    linear-gradient(90deg, rgba(14, 23, 38, 0.035) 1px, transparent 1px),
    linear-gradient(180deg, rgba(14, 23, 38, 0.035) 1px, transparent 1px),
    radial-gradient(circle at 10% 76%, rgba(249, 115, 22, 0.11), transparent 16%),
    radial-gradient(circle at 88% 18%, rgba(14, 165, 164, 0.11), transparent 18%),
    linear-gradient(180deg, var(--background) 0%, var(--surface) 100%);
  background-size: 32px 32px, 32px 32px, 100% 100%, 100% 100%, 100% 100%;

  &::before {
    content: '';
    position: absolute;
    width: 128px;
    height: 92px;
    right: 12%;
    top: 72px;
    opacity: 0.14;
    z-index: -1;
    background-image: radial-gradient(circle, var(--accent) 1.3px, transparent 1.3px);
    background-size: 15px 15px;
  }

  @media (max-width: 768px) {
    padding: 46px 14px 26px;
  }
`;

const FooterWrapper = styled.footer`
  width: min(1120px, 100%);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const MainCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(300px, 0.75fr);
  gap: 22px;
  padding: 24px;
  border-radius: 22px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 22px 60px rgba(72, 50, 28, 0.08);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 560px) {
    padding: 18px;
  }
`;

const BrandArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 8px 13px;
  border-radius: 999px;
  color: var(--accent);
  background: var(--orange-bg);
  border: 1px solid rgba(249, 115, 22, 0.22);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.13em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 5px rgba(249, 115, 22, 0.12);
  }
`;

const Logo = styled.a`
  width: fit-content;
  color: var(--text);
  font-family: 'Newsreader', Georgia, serif;
  font-size: clamp(34px, 4vw, 48px);
  line-height: 0.98;
  font-weight: 600;
  letter-spacing: -0.045em;
  text-decoration: none;

  span {
    color: var(--accent);
  }
`;

const Lead = styled.p`
  max-width: 650px;
  margin: 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.65;
  font-weight: 600;
`;

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
`;

const NavLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  color: var(--muted);
  background: var(--surface);
  border: 1px solid var(--border);
  font-size: 13px;
  font-weight: 650;
  text-decoration: none;
  transition: transform 0.22s ease, color 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    color: var(--accent);
    transform: translateY(-2px);
    border-color: rgba(249, 115, 22, 0.28);
    box-shadow: 0 12px 26px rgba(72, 50, 28, 0.08);
  }

  &:focus-visible {
    outline: 3px solid rgba(249, 115, 22, 0.2);
    outline-offset: 3px;
  }
`;

const ActionArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
`;

const NoteCard = styled.div`
  position: relative;
  padding: 22px;
  border-radius: 18px;
  background:
    radial-gradient(circle at top right, rgba(14, 165, 164, 0.1), transparent 35%),
    var(--surface-soft);
  border: 1px solid var(--border);
`;

const NoteLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--accent);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.13em;
  text-transform: uppercase;
`;

const NoteText = styled.p`
  margin: 13px 0 0;
  color: var(--text);
  font-size: 15px;
  line-height: 1.55;
  font-weight: 600;
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterButton = styled.a`
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 12px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 650;
  color: ${({ $primary }) => $primary ? 'var(--button-text)' : 'var(--text)'};
  background: ${({ $primary }) => $primary ? 'var(--button-bg)' : 'var(--surface)'};
  border: 1px solid ${({ $primary }) => $primary ? 'rgba(249, 115, 22, 0.36)' : 'var(--border)'};
  box-shadow: ${({ $primary }) => $primary ? '0 16px 34px rgba(15, 35, 61, 0.18)' : '0 12px 26px rgba(72, 50, 28, 0.06)'};
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(249, 115, 22, 0.36);
    box-shadow: 0 18px 36px rgba(72, 50, 28, 0.12);
  }
`;

const BottomStrip = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 18px;
  align-items: center;
  padding: 16px 20px;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 16px 40px rgba(72, 50, 28, 0.06);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
`;

const Copyright = styled.p`
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
  font-weight: 700;
`;

const SocialMediaIcons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const SocialMediaIcon = styled.a`
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ $color }) => $color};
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 10px 24px rgba(72, 50, 28, 0.06);
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(249, 115, 22, 0.3);
    box-shadow: 0 16px 30px rgba(72, 50, 28, 0.1);
  }

  &:focus-visible {
    outline: 3px solid rgba(249, 115, 22, 0.2);
    outline-offset: 3px;
  }
`;

const links = [
  { label: 'About', href: '#about' },
  { label: 'Projects', href: '#projects' },
  { label: 'Experience', href: '#experience' },
  { label: 'Skills', href: '#skills' },
  { label: 'Education', href: '#education' },
  { label: 'Research', href: '#research' },
  { label: 'Contact', href: '#contact' },
];

const socials = [
  { label: 'GitHub', href: Bio.github, icon: FaGithub, color: 'var(--text)' },
  { label: 'LinkedIn', href: Bio.linkedin, icon: FaLinkedinIn, color: '#0a66c2' },
  { label: 'Twitter', href: Bio.twitter, icon: FaTwitter, color: '#1d9bf0' },
  { label: 'Instagram', href: Bio.insta, icon: FaInstagram, color: '#e1306c' },
  { label: 'Facebook', href: Bio.facebook, icon: FaFacebookF, color: '#1877f2' },
];

function Footer() {
  const { pathname } = useLocation();
  const footerHref = href => pathname !== '/' && href.startsWith('#') ? getBasePath() + '/' + href : href;
  const reveal = useReveal();
  return (
    <FooterContainer>
      <FooterWrapper>
        <MainCard as={motion.div} {...reveal}>
          <BrandArea>
            <Eyebrow>Portfolio</Eyebrow>
            <Logo href={footerHref('#about')}>Kisho <span>Jeyapragash</span></Logo>
            <Lead>
              I’m Kisho Jeyapragash, a software engineer focused on backend development, full-stack applications, and practical AI integration.
            </Lead>
            <Nav aria-label="Footer navigation">
              {links.map((link) => (
                <NavLink key={link.href} href={footerHref(link.href)}>
                  {link.label}
                  <FiArrowUpRight aria-hidden="true" />
                </NavLink>
              ))}
            </Nav>
          </BrandArea>

          <ActionArea>
            <NoteCard>
              <NoteLabel>
                <FaPaperPlane />
                Open to
              </NoteLabel>
              <NoteText>I’m open to Software Engineer, Backend Developer, Full-Stack Developer, and AI Integration Engineer opportunities.</NoteText>
            </NoteCard>

            <ButtonRow>
              <FooterButton $primary href={footerHref('#contact')}>
                Contact Me
                <FaPaperPlane />
              </FooterButton>
              <FooterButton href={pathname === '/' ? Bio.resume : assetUrl(Bio.resume)} download>
                Download Resume
                <FiDownload />
              </FooterButton>
            </ButtonRow>
          </ActionArea>
        </MainCard>

        <BottomStrip>
          <Copyright>
            &copy; {new Date().getFullYear()} Kisho Jeyapragash. All rights reserved.
          </Copyright>
          <SocialMediaIcons>
            {socials.map((social) => {
              const Icon = social.icon;
              return (
                <SocialMediaIcon
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  $color={social.color}
                >
                  <Icon />
                </SocialMediaIcon>
              );
            })}
          </SocialMediaIcons>
        </BottomStrip>
      </FooterWrapper>
    </FooterContainer>
  );
}

export default Footer;




